import React from 'react';
import ReportAudiogram from '../../ReportAudiogram';
import { ptaAvg } from '../ptaCalc';

const SectionTitle = ({ children }) => (
  <div className="text-[11px] font-bold text-white bg-blue-700 px-2 py-0.5 border-b-2 border-blue-900 mb-1">
    {children}
  </div>
);

// -----------------------------------------------------------------------------
// PTA / SRT / SDS summary — reused across sidebar + stacked layouts. Kept
// intentionally small so it fits into the compact horizontal Info row.
// -----------------------------------------------------------------------------
const PTAMiniTable = ({ rightEar, leftEar }) => {
  const rP1 = ptaAvg(rightEar, 'ac_measurements', [500, 1000, 2000]);
  const rP2 = ptaAvg(rightEar, 'ac_measurements', [1000, 2000, 4000]);
  const lP1 = ptaAvg(leftEar,  'ac_measurements', [500, 1000, 2000]);
  const lP2 = ptaAvg(leftEar,  'ac_measurements', [1000, 2000, 4000]);
  const rB1 = ptaAvg(rightEar, 'bc_measurements', [500, 1000, 2000]);
  const lB1 = ptaAvg(leftEar,  'bc_measurements', [500, 1000, 2000]);
  const rABG = rP1 !== null && rB1 !== null ? rP1 - rB1 : null;
  const lABG = lP1 !== null && lB1 !== null ? lP1 - lB1 : null;
  const rSrt = rightEar?.srt_db ?? null;
  const lSrt = leftEar?.srt_db ?? null;
  const rSds = rightEar?.sds_percent ?? null;
  const lSds = leftEar?.sds_percent ?? null;
  return (
    <div className="border border-gray-300 rounded bg-white h-full">
      <div className="text-[10px] font-bold text-gray-800 bg-gray-100 text-center py-0.5 border-b border-gray-300">
        PTA / SRT / SDS (dB HL · %)
      </div>
      <table className="w-full text-[9px]">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-1 py-0.5 text-left font-semibold text-gray-600">Ear</th>
            <th className="px-1 py-0.5 font-semibold text-gray-700" title="500·1K·2K Hz">PTA1</th>
            <th className="px-1 py-0.5 font-semibold text-gray-700" title="1K·2K·4K Hz">PTA2</th>
            <th className="px-1 py-0.5 font-semibold text-gray-700" title="AC PTA1 − BC PTA1">ABG</th>
            <th className="px-1 py-0.5 font-semibold text-gray-700">SRT</th>
            <th className="px-1 py-0.5 font-semibold text-gray-700">SDS</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-gray-100">
            <td className="px-1 py-0.5 font-semibold text-red-600">R</td>
            <td className="px-1 py-0.5 text-center font-mono">{rP1 ?? '—'}</td>
            <td className="px-1 py-0.5 text-center font-mono">{rP2 ?? '—'}</td>
            <td className="px-1 py-0.5 text-center font-mono">{rABG ?? '—'}</td>
            <td className="px-1 py-0.5 text-center font-mono">{rSrt ?? '—'}</td>
            <td className="px-1 py-0.5 text-center font-mono">{rSds ?? '—'}</td>
          </tr>
          <tr>
            <td className="px-1 py-0.5 font-semibold text-blue-600">L</td>
            <td className="px-1 py-0.5 text-center font-mono">{lP1 ?? '—'}</td>
            <td className="px-1 py-0.5 text-center font-mono">{lP2 ?? '—'}</td>
            <td className="px-1 py-0.5 text-center font-mono">{lABG ?? '—'}</td>
            <td className="px-1 py-0.5 text-center font-mono">{lSrt ?? '—'}</td>
            <td className="px-1 py-0.5 text-center font-mono">{lSds ?? '—'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Tuning-fork mini table — unchanged from previous iteration, only trimmed to
// slot into the compact Info row without dominating it.
// -----------------------------------------------------------------------------
const TuningForkMiniTable = ({ tf }) => (
  <div className="border border-gray-300 rounded bg-white h-full">
    <div className="text-[10px] font-bold text-gray-800 bg-gray-100 text-center py-0.5 border-b border-gray-300">
      Tuning Fork
    </div>
    <table className="w-full text-[9px]">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="px-1 py-0.5 text-left font-semibold text-gray-600">Test</th>
          <th className="px-1 py-0.5 font-semibold text-red-600">R</th>
          <th className="px-1 py-0.5 font-semibold text-blue-600">L</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-gray-100">
          <td className="px-1 py-0.5 font-semibold text-gray-700">Rinne</td>
          <td className="px-1 py-0.5 text-center">{tf?.rinne_right ?? '—'}</td>
          <td className="px-1 py-0.5 text-center">{tf?.rinne_left  ?? '—'}</td>
        </tr>
        <tr>
          <td className="px-1 py-0.5 font-semibold text-gray-700">Weber</td>
          <td className="px-1 py-0.5 text-center" colSpan={2}>{tf?.weber ?? '—'}</td>
        </tr>
      </tbody>
    </table>
  </div>
);

// -----------------------------------------------------------------------------
// Compact horizontal Legend — occupies far less vertical space than the old
// sidebar block. Symbols share a single row, wrapping only if the paper is
// unusually narrow. Font is reduced to text-[8.5px] per the redesign spec.
// -----------------------------------------------------------------------------
const Legend = () => (
  <div className="border border-gray-300 rounded bg-gray-50 h-full flex flex-col">
    <div className="text-[10px] font-bold text-gray-800 bg-gray-100 text-center py-0.5 border-b border-gray-300">
      Legend
    </div>
    <div className="flex-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 px-1.5 py-1 text-[8.5px] text-gray-700 leading-tight">
      <span><span className="text-red-600 font-bold">O</span> R AC</span>
      <span><span className="text-red-600 font-bold">△</span> R AC (m)</span>
      <span><span className="text-red-600 font-bold">&lt;</span> R BC</span>
      <span><span className="text-blue-600 font-bold">X</span> L AC</span>
      <span><span className="text-blue-600 font-bold">□</span> L AC (m)</span>
      <span><span className="text-blue-600 font-bold">&gt;</span> L BC</span>
      <span className="text-gray-500">↙↘ NR</span>
    </div>
  </div>
);

export const PureToneSection = ({
  rightEar,
  leftEar,
  mode = 'combined',
  tuningFork,
  showTuningForkMini = false,
  size = 'standard',
}) => {
  // ---------------------------------------------------------------------
  // NEW layout (2026-09-01): audiogram(s) always span the full available
  // report width; Legend + PTA Summary (+ optional Tuning-fork mini) sit
  // in a COMPACT horizontal row underneath. Old sidebar layout retired —
  // the charts were being squeezed to ~60% of the page.
  // ---------------------------------------------------------------------
  //
  // Chart heights scale with the `size` toggle (COMBINED-view only — the
  // Split view now uses `aspect-ratio: 1/1` and inherits its dimensions
  // from the flex column width, so no explicit height is required):
  //   • standard → generous default that already fits an A4 with the
  //     Tympanogram section beneath.
  //   • large / xlarge → for reports where Tympanometry moves to its
  //     own page (BuilderSidebar exposes this toggle).
  const HEIGHTS = {
    combined: { standard: 360, large: 460, xlarge: 560 },
  };
  const chartHeight = HEIGHTS.combined[size] || HEIGHTS.combined.standard;

  // Compact horizontal Info row — used by BOTH split and combined layouts.
  // Legend on the left, PTA on the right (per the redesign spec). Tuning
  // fork mini optionally slots in as a third equal column.
  const InfoRow = (
    <div className="grid gap-2 mt-1.5 items-stretch"
         style={{ gridTemplateColumns: showTuningForkMini ? '1fr 1fr 1fr' : '1fr 1fr' }}
         data-testid="report-pure-tone-info-row">
      <Legend />
      <PTAMiniTable rightEar={rightEar} leftEar={leftEar} />
      {showTuningForkMini && <TuningForkMiniTable tf={tuningFork} />}
    </div>
  );

  if (mode === 'separate') {
    // Split-view charts are locked to a 1 : 1 aspect ratio so the audiogram
    // fills the tall available column and uses the previously-empty bottom
    // whitespace. Width flows from `flex-1` (~348 px on-screen inside the A4
    // preview); `aspect-ratio: 1/1` derives the matching height. Print CSS
    // (`App.css → @media print`) caps both dimensions so we still fit on a
    // single A4 page.
    return (
      <div data-testid="report-pure-tone-separate">
        <SectionTitle>Puretone Audiometry</SectionTitle>
        <div className="flex gap-2 items-start">
          <div className="flex-1" style={{ aspectRatio: '1 / 1' }} data-testid="report-pure-tone-chart-right">
            <ReportAudiogram rightEarData={rightEar} leftEarData={null} title="Right Ear" />
          </div>
          <div className="flex-1" style={{ aspectRatio: '1 / 1' }} data-testid="report-pure-tone-chart-left">
            <ReportAudiogram rightEarData={null} leftEarData={leftEar} title="Left Ear" />
          </div>
        </div>
        {InfoRow}
      </div>
    );
  }

  // combined — one full-width chart
  return (
    <div data-testid="report-pure-tone-combined">
      <SectionTitle>Puretone Audiometry</SectionTitle>
      <div style={{ height: `${chartHeight}px` }} data-testid="report-pure-tone-chart-combined">
        <ReportAudiogram rightEarData={rightEar} leftEarData={leftEar} />
      </div>
      {InfoRow}
    </div>
  );
};
